<template>
    <div class="xScrollable">
        <table class="kgr-table kgr-table--block kgr-table--striped data-table table-groups printable">
            <thead>
                <tr class="kgr-table__row">
                    <td class="kgr-table__cell">
                        <input
                            type="checkbox"
                            @change="$emit('selectAll')"
                        />
                    </td>
                    <td
                        class="kgr-table__cell"
                        :colspan="colspan"
                    >
                        <div class="kgr-accordion__itemHeadline">
                            &nbsp;{{ headline }}
                            <kgr-icon
                                class="kgr-icon kgr-icon--tiny openConfig"
                                icon-class="settings"
                                :title="tOpenTableConfig"
                                @click="openTableConfigurationDialog()"
                            />
                        </div>
                    </td>
                </tr>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                    </th>
                    <th
                        v-show="showColumn(TableColumn.NAME)"
                        class="kgr-table__cell kgr-table__cell--headline"
                    >
                        {{ tBookingName }}
                    </th>
                    <th
                        v-show="showColumn(TableColumn.CONTRACT_STATUS)"
                        class="kgr-table__cell kgr-table__cell--headline"
                    >
                        {{ tBookingContractStatus }}
                    </th>
                    <th
                        v-show="showColumn(TableColumn.AGE)"
                        class="kgr-table__cell kgr-table__cell--headline"
                    >
                        {{ tBookingAge }}
                    </th>
                    <th
                        v-show="showColumn(TableColumn.EMAIL)"
                        class="kgr-table__cell kgr-table__cell--headline"
                    >
                        {{ tBookingEmail }}
                    </th>
                    <th
                        v-show="showColumn(TableColumn.PHONE)"
                        class="kgr-table__cell kgr-table__cell--headline"
                    >
                        {{ tBookingPhone }}
                    </th>
                    <th
                        v-show="showColumn(TableColumn.PHONE_MOBILE)"
                        class="kgr-table__cell kgr-table__cell--headline"
                    >
                        {{ tBookingPhoneMobile }}
                    </th>
                    <th
                        v-show="showColumn(TableColumn.EMERGENCY_CONTACT)"
                        class="kgr-table__cell kgr-table__cell--headline"
                    >
                        {{ tEmergencyContact }}
                    </th>
                    <th
                        v-show="showColumn(TableColumn.REPLIED_AT)"
                        class="kgr-table__cell kgr-table__cell--headline"
                    >
                        {{ tBookingRepliedAt }}
                    </th>
                    <th
                        v-show="showColumn(TableColumn.ATTENDEE_STATUS)"
                        class="kgr-table__cell kgr-table__cell--headline"
                    >
                        {{ tBookingStatus }}
                    </th>
                    <th
                        v-show="showColumn(TableColumn.COMMENT)"
                        class="kgr-table__cell kgr-table__cell--headline"
                    >
                        {{ tBookingComment }}
                    </th>
                    <th
                        v-show="showColumn(TableColumn.CONTRACT_END)"
                        class="kgr-table__cell kgr-table__cell--headline"
                    >
                        {{ tBookingContractEnd }}
                    </th>
                    <th
                        v-show="showColumn(TableColumn.DEPARTMENTS)"
                        class="kgr-table__cell kgr-table__cell--headline"
                    >
                        {{ tBookingDepartments }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ tBookingAction }}
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-if="pending"
                >
                    <td :colspan="colspan">
                        <kgr-loading />
                    </td>
                </tr>
                <template v-else>
                    <tr
                        v-for="booking in bookings"
                        :key="booking.id"
                        class="kgr-table__row"
                    >
                        <td class="kgr-table__cell">
                            <input
                                :id="booking.member.id"
                                v-model="booking.checked"
                                type="checkbox"
                                :value="booking.member.email"
                                @change="$emit('bookingChecked', {id: booking.id, checked: booking.checked})"
                            />
                        </td>
                        <td
                            v-show="showColumn(TableColumn.NAME)"
                            class="kgr-table__cell"
                        >
                            <a
                                v-if="booking.canViewShortProfile"
                                :href="memberLink(booking.member.id)"
                                target="_blank"
                                class="kgr-link"
                            >
                                {{ booking.member.fullName() }}
                            </a>
                            <template v-else>
                                {{ booking.member.fullName() }}
                            </template>
                        </td>
                        <td
                            v-show="showColumn(TableColumn.CONTRACT_STATUS)"
                            class="kgr-table__cell kgr-table__cell--noGrow"
                            :data-order="booking.member.contractState.status"
                        >
                            <kgr-member-contract-state-icon :state="booking.member.contractState" />
                        </td>
                        <td
                            v-show="showColumn(TableColumn.AGE)"
                            class="kgr-table__cell"
                        >
                            {{ booking.member.age }}
                        </td>
                        <td
                            v-show="showColumn(TableColumn.EMAIL)"
                            class="kgr-table__cell"
                        >
                            <a
                                v-if="booking.member.email"
                                :href="'mailto:' + booking.member.email"
                                @click="$emit('copyMailAddress', booking.member.email)"
                                :title="booking.member.email"
                            >
                                <kgr-icon
                                    class="kgr-icon kgr-icon--small"
                                    icon-class="email"
                                />
                            </a>
                        </td>
                        <td
                            v-show="showColumn(TableColumn.PHONE)"
                            class="kgr-table__cell"
                        >
                            {{ booking.member.phone }}
                        </td>
                        <td
                            v-show="showColumn(TableColumn.PHONE_MOBILE)"
                            class="kgr-table__cell"
                        >
                            {{ booking.member.phoneMobile }}
                        </td>
                        <td
                            v-show="showColumn(TableColumn.EMERGENCY_CONTACT)"
                            class="kgr-table__cell"
                        >
                            {{ booking.member.emergencyContact }}
                        </td>
                        <td
                            v-show="showColumn(TableColumn.REPLIED_AT)"
                            class="kgr-table__cell"
                        >
                            {{ getBookedAtDate(booking) }}
                        </td>
                        <td
                            v-show="showColumn(TableColumn.ATTENDEE_STATUS)"
                            class="kgr-table__cell"
                        >
                            {{ $t('attendanceList.member.status.' + booking.attendeeStatus) }}
                        </td>
                        <td
                            v-show="showColumn(TableColumn.COMMENT)"
                            class="kgr-table__cell"
                        >
                            {{ booking.comment }}
                        </td>
                        <td
                            v-show="showColumn(TableColumn.CONTRACT_END)"
                            class="kgr-table__cell"
                        >
                            {{ formatContractEnd(booking.member.contractEnd) }}
                        </td>
                        <td
                            v-show="showColumn(TableColumn.DEPARTMENTS)"
                            class="kgr-table__cell"
                        >
                            {{ booking.member.departments.join(', ') }}
                        </td>
                        <td class="kgr-table__cell">
                            <kgr-icon
                                v-if="canReject"
                                class="kgr-icon"
                                icon-class="cancel"
                                :title="$t('offering.cancelBooking')"
                                @click="$emit('rejectBooking', booking)"
                            />
                            <kgr-icon
                                v-if="canAccept"
                                class="kgr-icon"
                                icon-class="check"
                                :title="$t('offering.acceptBooking')"
                                @click="$emit('acceptBooking', booking)"
                            />
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
        <br />
        <kgr-table-configuration
            ref="tableConfiguration"
            :offering="offering"
            :table-columns="tableColumns"
            :user-id="userId"
            :show-dialog="showTableConfiguration"
            :type="tableType"
            @change="updateColumns"
            @closeDialog="closeTableConfigurationDialog()"
        />
    </div>
</template>

<script>
import Icon from '../../common/icon/Icon.vue';
import KgrMemberContractStateIcon from '../../members/MemberContractStateIcon.vue';
import Loading from '../../common/Loading.vue';
import Offering from '../../../js/dto/offering/Offering.js';
import TableColumn from '../../../js/dto/offering/TableColumn.js';
import TableConfiguration from './TableConfiguration.vue';
import moment from 'moment';

export default {
    name: 'KgrTrainingAppointmentParticipantTable',
    components: {
        'kgr-icon': Icon,
        'kgr-loading': Loading,
        'kgr-member-contract-state-icon': KgrMemberContractStateIcon,
        'kgr-table-configuration': TableConfiguration,
    },
    props: {
        offering: {
            type: Offering,
            required: true,
        },
        bookings: {
            type: Array,
            required: true,
        },
        headline: {
            type: String,
            required: true,
        },
        canAccept: {
            type: Boolean,
            required: false,
            default: false
        },
        canReject: {
            type: Boolean,
            required: false,
        },
        pending: {
            type: Boolean,
            required: false,
            default: false
        },
        userId: {
            type: Number,
            required: true,
        },
        tableColumns: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            showTableConfiguration: false,
            selectedColumns: null,
        }
    },
    computed: {
        TableColumn () {
            return TableColumn;
        },
        colspan() {
            if (!this.selectedColumns) {
                return 13;
            }
            return this.selectedColumns.length + 1;
        },
        tOpenTableConfig() {
            return this.$t('offering.tableConfig');
        },
        tBookingName() {
            return this.$t('Name');
        },
        tBookingContractStatus() {
            return this.$t('booking.contractStatus');
        },
        tBookingAge() {
            return this.$t('booking.age');
        },
        tBookingEmail() {
            return this.$t('booking.email');
        },
        tBookingPhone() {
            return this.$t('booking.phone');
        },
        tBookingPhoneMobile() {
            return this.$t('booking.phoneMobile');
        },
        tEmergencyContact() {
            return this.$t('booking.emergencyContact');
        },
        tBookingRepliedAt() {
            return this.$t('booking.repliedAt');
        },
        tBookingStatus() {
            return this.$t('booking.attendeeStatus');
        },
        tBookingComment() {
            return this.$t('booking.comment');
        },
        tBookingContractEnd() {
            return this.$t('booking.contractEnd');
        },
        tBookingDepartments() {
            return this.$t('Abteilungen');
        },
        tBookingAction() {
            return this.$t('Aktion');
        },
        tableType() {
            if (this.canReject && !this.canAccept) {
                return TableColumn.TABLE_TYPE_ACCEPTED;
            } else if (!this.canReject && this.canAccept) {
                return TableColumn.TABLE_TYPE_REJECTED;
            }
            return TableColumn.TABLE_TYPE_UNSURE;
        },
    },
    methods: {
        memberLink(memberId) {
            return `/backend/clubs/members/${memberId}/show`;
        },
        formattedDateTime(date) {
            return moment(date, 'YYYY-MM-DD HH:mm:ss').format('DD.MM.YYYY HH:mm');
        },
        getBookedAtDate(booking) {
            const formattedDateTime = this.formattedDateTime(booking.respondedAt.date);

            if (formattedDateTime === '01.01.1970 00:00') {
                return this.$t('booking.confirmationByClub');
            }

            return formattedDateTime
                + ' ('
                + (booking.bookedByClub ? this.$t('Verein') : this.$t('booking.bookedByParticipant'))
                + ')';
        },
        openTableConfigurationDialog() {
            this.showTableConfiguration = true;
        },
        closeTableConfigurationDialog() {
            this.showTableConfiguration = false;
        },
        updateColumns() {
            this.selectedColumns = this.$refs.tableConfiguration.loadConfiguration();
        },
        showColumn(column) {
            if (!this.selectedColumns) {
                return true;
            }
            return this.selectedColumns.includes(column);
        },
        getSelectedColumns() {
            return this.selectedColumns;
        },
        formatContractEnd(contractEnd) {
            return contractEnd ? moment(contractEnd, 'YYYY-MM-DD').format('DD.MM.YYYY') : '';
        }
    },
    mounted() {
        this.updateColumns()
    },
}
</script>

<style scoped lang="scss" rel="stylesheet/scss">
.openConfig {
    float: right;
    margin-right: 20px;
}
.xScrollable {
    overflow-x: auto;
}
</style>
